import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  AuthzPolicy,
  AuthzUserProfile,
  useCreateAdminWithUserProfileMutation,
  useCreatePoliciesMutation,
  useCreateRolesMutation,
  useDeleteUserProfileMutation,
  useGetAuthzMetadataQuery,
  useGetPoliciesQuery,
  useGetRolesQuery,
  useGetUserPoliciesQuery,
  useGetUserProfilesQuery,
  useUpdatePolicyMutation,
  useUpdateRoleMutation,
  useUpdateUserProfileMutation,
} from "../../actions/authz.action";
import Alert from "../../components/alert";
import Badge from "../../components/badge";
import Button from "../../components/button";
import { SearchBox, Select } from "../../components/input-functions";
import Pagination from "../../components/pagination";
import Table from "../../components/table";
import Tabs from "../../components/tabs";
import useScreenName from "../../hooks/useScreenName";
import { ReduxState } from "../../reducers";
import AddPortalUserModal from "./addPortalUserModal";
import {
  EntityEditItem,
  EntityEditorModal,
  RemoveUserModal,
  RoleChangeConfirmModal,
} from "./modals";
import "./styles.css";

type TabKey = "users" | "roles" | "policies" | "lookup";

interface Banner {
  type: "success" | "danger";
  message: string;
}

function getErrorMessage(err: unknown): string {
  if (!err) return "";
  const message = (err as { data?: { message?: string }; message?: string })
    .data?.message;
  if (message) return message;
  return (err as { message?: string }).message || "Something went wrong";
}

export default function PortalAccessPage() {
  useScreenName("Portal Access");
  const adminId = useSelector(
    (state: ReduxState) => state.admin.data?._id || "",
  );
  const adminRoles = useSelector(
    (state: ReduxState) => state.admin.data?.roles || [],
  );
  const canAccess =
    adminRoles.includes("ADMIN") || adminRoles.includes("SUPER_ADMIN");

  const [tab, setTab] = useState<TabKey>("users");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [roleChange, setRoleChange] = useState<{
    profile: AuthzUserProfile;
    nextRole: string;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AuthzUserProfile | null>(
    null,
  );
  const [editItem, setEditItem] = useState<EntityEditItem | null>(null);
  const [lookupAdminId, setLookupAdminId] = useState(adminId);

  const profilesQ = useGetUserProfilesQuery(
    {
      search,
      role: roleFilter || undefined,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    { skip: !canAccess },
  );
  const metadataQ = useGetAuthzMetadataQuery(undefined, { skip: !canAccess });
  const rolesQ = useGetRolesQuery(
    { limit: 1000, offset: 0 },
    { skip: !canAccess },
  );
  const policiesQ = useGetPoliciesQuery(
    { limit: 1000, offset: 0 },
    { skip: !canAccess },
  );
  const lookupQ = useGetUserPoliciesQuery(
    { adminId: lookupAdminId },
    { skip: !canAccess || !lookupAdminId },
  );

  const [createAdminWithUserProfile, { isLoading: isCreatingUser }] =
    useCreateAdminWithUserProfileMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] =
    useUpdateUserProfileMutation();
  const [deleteUserProfile, { isLoading: isDeletingProfile }] =
    useDeleteUserProfileMutation();
  const [createRole, { isLoading: isCreatingRole }] = useCreateRolesMutation();
  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateRoleMutation();
  const [createPolicy, { isLoading: isCreatingPolicy }] =
    useCreatePoliciesMutation();
  const [updatePolicy, { isLoading: isUpdatingPolicy }] =
    useUpdatePolicyMutation();

  const [createError, setCreateError] = useState("");
  const [roleChangeError, setRoleChangeError] = useState("");
  const [removeError, setRemoveError] = useState("");
  const [entityError, setEntityError] = useState("");

  const roleOptions = useMemo(
    () =>
      (rolesQ.data?.items || []).map((role) => ({
        value: role._id,
        label: role.name,
      })),
    [rolesQ.data],
  );

  const roleNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (rolesQ.data?.items || []).forEach((role) => {
      map[role._id] = role.name;
    });
    return map;
  }, [rolesQ.data]);

  const policyNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (policiesQ.data?.items || []).forEach((policy) => {
      map[policy._id] = `${policy.resource} · ${policy.action}`;
    });
    return map;
  }, [policiesQ.data]);

  const resourceOptions = useMemo(
    () =>
      (metadataQ.data?.resources || []).map((r) => ({ value: r, label: r })),
    [metadataQ.data],
  );

  const actionOptions = useMemo(
    () => (metadataQ.data?.actions || []).map((a) => ({ value: a, label: a })),
    [metadataQ.data],
  );

  const effectOptions = useMemo(
    () => (metadataQ.data?.effects || []).map((e) => ({ value: e, label: e })),
    [metadataQ.data],
  );

  const visibleProfiles = useMemo(
    () => profilesQ.data?.items || [],
    [profilesQ.data],
  );

  const roleSelectOptions = useMemo(() => {
    const byId = new Map<string, string>(
      roleOptions.map((role) => [role.value, role.label]),
    );
    visibleProfiles.forEach((profile) => {
      if (profile.role && !byId.has(profile.role)) {
        byId.set(profile.role, profile.role);
      }
    });
    return Array.from(byId, ([value, label]) => ({ value, label }));
  }, [roleOptions, visibleProfiles]);

  const totalEntries = useMemo(
    () => profilesQ.data?.total ?? profilesQ.data?.items.length ?? 0,
    [profilesQ.data],
  );

  const userColumns = useMemo(
    () => [
      { field: "user", label: "User / Email" },
      { field: "adminId", label: "Admin ID" },
      { field: "role", label: "Role" },
      { field: "actions", label: "Actions" },
    ],
    [],
  );

  const userRows = useMemo(() => {
    return visibleProfiles.map((profile) => ({
      _id: profile._id,
      user: (
        <div>
          <div className="rf-authz-user-name">{profile.fullName}</div>
          <div className="text-muted" style={{ fontSize: "0.85em" }}>
            {profile.email}
          </div>
        </div>
      ),
      adminId: <span className="text-muted">{profile.adminId}</span>,
      role: (
        <Select
          className="form-control-sm rf-authz-inline-role"
          value={profile.role || ""}
          options={roleSelectOptions}
          onChange={(e) => {
            const nextRole = e.target.value;
            if (nextRole && nextRole !== profile.role) {
              setRoleChange({ profile, nextRole });
            }
          }}
          disabled={profile.adminId === adminId}
          title={
            profile.adminId === adminId
              ? "You cannot change your own role"
              : undefined
          }
        />
      ),
      actions: (
        <Button
          size="xs"
          variant="danger-text"
          disabled={profile.adminId === adminId}
          title={
            profile.adminId === adminId
              ? "You cannot remove your own account"
              : "Remove user"
          }
          onClick={() => setRemoveTarget(profile)}
        >
          Remove
        </Button>
      ),
    }));
  }, [visibleProfiles, roleSelectOptions, adminId]);

  const isLoading =
    tab === "users"
      ? profilesQ.isLoading
      : tab === "roles"
        ? rolesQ.isLoading
        : tab === "policies"
          ? policiesQ.isLoading
          : lookupQ.isLoading;

  function resetFilters() {
    setPage(1);
  }

  function handleSearch(next: string) {
    setSearch(next);
    resetFilters();
  }

  function handleRoleFilter(next: string) {
    setRoleFilter(next);
    resetFilters();
  }

  function handleOpenAddUser() {
    setCreateError("");
    setAddUserOpen(true);
  }

  async function handleAddUser(
    body: Parameters<typeof createAdminWithUserProfile>[0],
  ) {
    setCreateError("");
    try {
      const { userProfile } = await createAdminWithUserProfile(body).unwrap();
      setAddUserOpen(false);
      setBanner({
        type: "success",
        message: `${userProfile.fullName} added as a portal user.`,
      });
    } catch (err) {
      setCreateError(getErrorMessage(err));
    }
  }

  async function handleConfirmRoleChange() {
    if (!roleChange) return;
    setRoleChangeError("");
    const previousName =
      roleNameById[roleChange.profile.role || ""] ||
      roleChange.profile.role ||
      "—";
    const nextName = roleNameById[roleChange.nextRole] || roleChange.nextRole;
    try {
      await updateProfile({
        id: roleChange.profile._id,
        body: { role: roleChange.nextRole },
      }).unwrap();
      setBanner({
        type: "success",
        message: `${roleChange.profile.fullName}'s role changed from ${previousName} to ${nextName}.`,
      });
      setRoleChange(null);
    } catch (err) {
      setRoleChangeError(getErrorMessage(err));
    }
  }

  async function handleConfirmRemove() {
    if (!removeTarget) return;
    setRemoveError("");
    try {
      await deleteUserProfile(removeTarget._id).unwrap();
      setBanner({
        type: "success",
        message: `${removeTarget.fullName} removed. Access revoked, sessions terminated and audit trail retained.`,
      });
      setRemoveTarget(null);
    } catch (err) {
      setRemoveError(getErrorMessage(err));
    }
  }

  async function handleSaveEntity(payload: {
    kind: "role" | "policy";
    _id?: string;
    name?: string;
    policies?: string[];
    resource?: string;
    effect?: string;
    action?: string;
    conditions?: unknown[];
  }) {
    setEntityError("");
    try {
      if (payload.kind === "role") {
        if (payload._id) {
          await updateRole({
            id: payload._id,
            body: { name: payload.name, policies: payload.policies || [] },
          }).unwrap();
        } else {
          await createRole({
            roles: [
              { name: payload.name || "", policies: payload.policies || [] },
            ],
          }).unwrap();
        }
      } else {
        if (payload._id) {
          await updatePolicy({
            id: payload._id,
            body: {
              resource: payload.resource,
              effect: payload.effect,
              action: payload.action,
              conditions: payload.conditions,
            },
          }).unwrap();
        } else {
          await createPolicy({
            authPolicies: [
              {
                resource: payload.resource || "",
                effect: payload.effect || "",
                action: payload.action || "",
                conditions: payload.conditions || [],
              },
            ],
          }).unwrap();
        }
      }
      setBanner({
        type: "success",
        message: `${payload.kind === "role" ? "Role" : "Policy"} saved.`,
      });
      setEditItem(null);
    } catch (err) {
      setEntityError(getErrorMessage(err));
    }
  }

  function handleSearchEntity() {
    if (lookupAdminId) {
      lookupQ.refetch();
    }
  }

  if (!canAccess) {
    return (
      <div>
        <h2 className="mb-4">Portal Access</h2>
        <div className="dashboard-container">
          <Alert type="danger">
            You do not have permission to view this interface. Only users with
            an Admin role can manage portal users.
          </Alert>
        </div>
      </div>
    );
  }

  const entitySubmitting =
    isCreatingRole || isUpdatingRole || isCreatingPolicy || isUpdatingPolicy;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h2 className="mb-0">Portal Access</h2>
        {tab === "users" && (
          <Button variant="blue" onClick={handleOpenAddUser}>
            Add User
          </Button>
        )}
        {tab === "roles" && (
          <Button
            variant="blue"
            onClick={() => setEditItem({ kind: "role", create: true })}
          >
            Create Role
          </Button>
        )}
        {tab === "policies" && (
          <Button
            variant="blue"
            onClick={() => setEditItem({ kind: "policy", create: true })}
          >
            Create Policy
          </Button>
        )}
      </div>
      <div className="dashboard-container">
        {banner && (
          <div className="mb-3">
            <Alert
              type={banner.type}
              canDismiss
              onDismissClick={() => setBanner(null)}
            >
              {banner.message}
            </Alert>
          </div>
        )}

        <div className="mb-4">
          <Tabs<TabKey>
            items={[
              { label: "Users", value: "users" },
              { label: "Roles", value: "roles" },
              { label: "Policies", value: "policies" },
              { label: "User Policies", value: "lookup" },
            ]}
            value={tab}
            onChange={(next) => setTab(next)}
          />
        </div>

        {tab === "users" && (
          <>
            <div className="mb-3 d-flex flex-wrap align-items-end">
              <div className="mr-3 mb-2">
                <SearchBox
                  placeholder="Search by name or email"
                  value={search}
                  onSubmit={handleSearch}
                />
              </div>
              <div className="mr-3 mb-2 rf-authz-filter">
                <label className="rf-authz-filter-label">Role</label>
                <Select
                  className="form-control-sm"
                  placeholder="All roles"
                  options={roleOptions}
                  value={roleFilter}
                  onChange={(e) => handleRoleFilter(e.target.value)}
                />
              </div>
            </div>

            {profilesQ.isError ? (
              <Alert type="danger">
                Failed to load portal users. Please retry.
              </Alert>
            ) : (
              <>
                <Table
                  loading={isLoading}
                  columns={userColumns}
                  data={userRows as any[]}
                  keyField="_id"
                />
                <Pagination
                  currentPage={page}
                  totalEntries={totalEntries}
                  numberOfEntriesPerPage={perPage}
                  onPageChange={(next) => setPage(next)}
                  onPerPageCountChange={(next) => {
                    setPerPage(next);
                    setPage(1);
                  }}
                />
              </>
            )}
          </>
        )}

        {tab === "roles" && (
          <Table
            loading={isLoading}
            keyField="_id"
            columns={[
              { field: "name", label: "Name" },
              { field: "policies", label: "Policies" },
              { field: "actions", label: "Actions" },
            ]}
            data={(rolesQ.data?.items || []).map((role) => {
              const rolePolicies = (role.policies || []).filter(
                (policyId) => policyNameById[policyId],
              );
              const staleCount =
                (role.policies || []).length - rolePolicies.length;
              return {
                _id: role._id,
                name: role.name,
                policies: (
                  <div className="d-flex flex-wrap">
                    {rolePolicies.length > 0 &&
                      rolePolicies.map((policyId) => (
                        <span
                          key={policyId}
                          className="rf-authz-chip mr-1 mb-1"
                          title={policyNameById[policyId]}
                        >
                          {policyNameById[policyId]}
                        </span>
                      ))}
                    {rolePolicies.length === 0 && (
                      <span className="text-muted">No policies</span>
                    )}
                    {staleCount > 0 && (
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.8em" }}
                        title={`${staleCount} policy reference(s) no longer exist`}
                      >
                        +{staleCount} missing
                      </span>
                    )}
                  </div>
                ),
                actions: (
                  <Button
                    size="xs"
                    variant="blue-text"
                    onClick={() =>
                      setEditItem({ kind: "role", create: false, entity: role })
                    }
                  >
                    Edit
                  </Button>
                ),
              };
            })}
          />
        )}

        {tab === "policies" && (
          <Table
            loading={isLoading}
            keyField="_id"
            columns={[
              { field: "resource", label: "Resource" },
              { field: "effect", label: "Effect" },
              { field: "action", label: "Action" },
              { field: "actions", label: "Actions" },
            ]}
            data={
              (policiesQ.data?.items || []).map((policy) => ({
                _id: policy._id,
                resource: policy.resource,
                effect: (
                  <Badge
                    type={policy.effect === "ALLOW" ? "success" : "reject"}
                  >
                    {policy.effect}
                  </Badge>
                ),
                action: policy.action,
                actions: (
                  <Button
                    size="xs"
                    variant="blue-text"
                    onClick={() =>
                      setEditItem({
                        kind: "policy",
                        create: false,
                        entity: policy,
                      })
                    }
                  >
                    Edit
                  </Button>
                ),
              })) as any[]
            }
          />
        )}

        {tab === "lookup" && (
          <div>
            <div className="d-flex mb-3" style={{ maxWidth: "420px" }}>
              <input
                className="form-control mr-2"
                aria-label="Admin ID"
                value={lookupAdminId}
                onChange={(e) => setLookupAdminId(e.target.value)}
                placeholder="Admin ID"
              />
              <Button onClick={handleSearchEntity} loading={lookupQ.isFetching}>
                Lookup
              </Button>
            </div>
            {lookupQ.isError && (
              <Alert type="danger">
                Failed to load policies for this admin id. Please retry.
              </Alert>
            )}
            {!lookupQ.isLoading &&
              !lookupQ.isError &&
              !lookupQ.data?.userPolicies?.length && (
                <p className="text-muted">
                  No policy data found for this admin id.
                </p>
              )}
            {lookupQ.data?.actor && (
              <div className="mb-2">
                <Badge type="success">
                  {lookupQ.data.actor.fullName} · {lookupQ.data.actor.email}
                </Badge>
              </div>
            )}
            {lookupQ.data?.userPolicies &&
              lookupQ.data.userPolicies.length > 0 && (
                <div className="d-flex flex-wrap mb-3">
                  {(lookupQ.data.userPolicies as AuthzPolicy[]).map(
                    (policy) => (
                      <span
                        key={policy._id}
                        className="rf-authz-chip mr-1 mb-1"
                      >
                        {policy.resource} · {policy.effect} · {policy.action}
                      </span>
                    ),
                  )}
                </div>
              )}
          </div>
        )}

        <AddPortalUserModal
          key={addUserOpen ? "add-user-open" : "add-user-closed"}
          open={addUserOpen}
          roles={rolesQ.data?.items || []}
          submitting={isCreatingUser}
          submitError={createError}
          onClose={() => setAddUserOpen(false)}
          onSubmit={handleAddUser}
        />

        <RoleChangeConfirmModal
          key={
            roleChange
              ? `${roleChange.profile._id}-${roleChange.nextRole}`
              : "role-change-none"
          }
          open={roleChange !== null}
          profile={roleChange?.profile || null}
          previousRoleName={
            roleChange
              ? roleNameById[roleChange.profile.role || ""] ||
                roleChange.profile.role ||
                "—"
              : ""
          }
          nextRoleName={
            roleChange
              ? roleNameById[roleChange.nextRole] || roleChange.nextRole
              : ""
          }
          submitting={isUpdatingProfile}
          submitError={roleChangeError}
          onConfirm={handleConfirmRoleChange}
          onCancel={() => setRoleChange(null)}
        />

        <RemoveUserModal
          key={removeTarget?._id || "remove-none"}
          open={removeTarget !== null}
          profile={removeTarget}
          submitting={isDeletingProfile}
          submitError={removeError}
          onConfirm={handleConfirmRemove}
          onCancel={() => setRemoveTarget(null)}
        />

        <EntityEditorModal
          key={
            editItem
              ? `${editItem.kind}-${editItem.entity?._id || "new"}`
              : "entity-none"
          }
          open={editItem !== null}
          item={editItem}
          policyOptions={
            policiesQ.data?.items.map((policy) => ({
              value: policy._id,
              label: `${policy.resource} · ${policy.action}`,
            })) || []
          }
          resourceOptions={resourceOptions}
          actionOptions={actionOptions}
          effectOptions={effectOptions}
          submitting={entitySubmitting}
          submitError={entityError}
          onClose={() => setEditItem(null)}
          onSave={handleSaveEntity}
        />
      </div>
    </div>
  );
}
